import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faMapLocationDot } from "@fortawesome/pro-solid-svg-icons/faMapLocationDot";
import { faHeart as faHeartRegular } from "@fortawesome/pro-regular-svg-icons/faHeart";
import { faHeart as faHeartSolid } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { useTranslation } from "react-i18next";
import { cn } from "@wondermaps/ui";

export interface POISheetHeaderProps {
  title: string;
  subtitle?: { icon: IconDefinition; text: string }[];
  isBookmarked?: boolean;
  onBookmarkToggle?: () => void;
  onClose: () => void;
  onBack?: () => void;
  /** Show map-pin icon (list → map context switch) */
  onViewOnMap?: () => void;
  bookmarksEnabled?: boolean;
  className?: string;
  /**
   * Text / icon color for dark (brand-accented) backgrounds.
   * When omitted, defaults to CSS inherit (works with Tailwind light-mode classes).
   */
  fg?: string;
  /**
   * Background for the close button pill.
   * Defaults to bg-foreground/[0.08] (light surfaces).
   */
  closePillBg?: string;
  /**
   * Opacity applied to subtitle spans.
   * When omitted, `text-muted-foreground` class is used instead.
   */
  subtitleOpacity?: number;
}

export function POISheetHeader({
  title,
  subtitle,
  isBookmarked,
  onBookmarkToggle,
  onClose,
  onBack,
  onViewOnMap,
  bookmarksEnabled,
  className,
  fg,
  closePillBg,
  subtitleOpacity,
}: POISheetHeaderProps) {
  const { t } = useTranslation(["common", "map", "pois"]);
  const isDark = fg !== undefined;

  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      <div className="flex items-start gap-2">
        {/* Back */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="p-2 -m-2 flex items-center justify-center shrink-0 min-h-[44px] min-w-[44px]"
            aria-label={t("common:back")}
            style={fg ? { color: fg } : undefined}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          </button>
        )}

        {/* Title */}
        <h2
          className="text-[20px] font-semibold leading-snug flex-1 break-words"
          style={fg ? { color: fg } : undefined}
        >
          {title}
        </h2>

        {/* Map icon + heart + X */}
        <div className="flex items-center gap-3 shrink-0">
          {onViewOnMap && (
            <button
              type="button"
              onClick={onViewOnMap}
              className="p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px]"
              aria-label={t("pois:viewOnMap")}
            >
              <FontAwesomeIcon
                icon={faMapLocationDot}
                className="w-5 h-5"
                style={isDark ? { color: fg, opacity: 0.5 } : { opacity: 0.16 }}
              />
            </button>
          )}

          {bookmarksEnabled && onBookmarkToggle && (
            <button
              type="button"
              onClick={onBookmarkToggle}
              className="p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px] active:scale-90 transition-transform"
              aria-label={isBookmarked ? t("common:removeSaved") : t("common:save")}
              aria-pressed={isBookmarked}
            >
              <FontAwesomeIcon
                icon={isBookmarked ? faHeartSolid : faHeartRegular}
                className="w-5 h-5"
                style={
                  isBookmarked
                    ? { color: "#f87171" }
                    : isDark
                    ? { color: fg, opacity: 0.5 }
                    : { opacity: 0.16 }
                }
              />
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px]"
            aria-label={t("common:close")}
          >
            <span
              className={cn(
                "rounded-full p-1 flex items-center justify-center",
                !closePillBg && "bg-foreground/[0.08]"
              )}
              style={closePillBg ? { backgroundColor: closePillBg } : undefined}
            >
              <FontAwesomeIcon
                icon={faXmark}
                className="w-3.5 h-3.5"
                style={fg ? { color: fg } : undefined}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Subtitle pills */}
      {subtitle && subtitle.length > 0 && (
        <div className="flex items-center gap-3">
          {subtitle.map((item, i) => (
            <span
              key={i}
              className={cn(
                "inline-flex items-center gap-1.5 text-xs",
                subtitleOpacity === undefined && "text-muted-foreground"
              )}
              style={
                subtitleOpacity !== undefined
                  ? { opacity: subtitleOpacity, color: fg }
                  : undefined
              }
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={cn(
                  "h-3.5 w-3.5",
                  subtitleOpacity === undefined && "text-foreground/[0.32]"
                )}
              />
              {item.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
